import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Truck, BadgePercent, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = ["Wedding", "Birthday", "Corporate", "School", "Festival", "Other"];

export default function BulkOrder() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_type: "Wedding", quantity: "", event_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.quantity) {
      toast.error("Name, phone & quantity are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bulk-order`, form);
      toast.success("Quote request saved! Opening WhatsApp…");
      window.open(data.whatsapp_url, "_blank");
      setForm({ name: "", phone: "", email: "", event_type: "Wedding", quantity: "", event_date: "", notes: "" });
    } catch {
      toast.error("Try calling 9235372759 directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-12 md:py-16" data-testid="bulk-order-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero strip */}
        <div className="bg-holiday-red text-white rounded-[2.5rem] p-8 md:p-12 cushion relative overflow-hidden mb-12" data-testid="bulk-hero">
          <span className="sticker bg-white text-holiday-red animate-wobble">Discount on Bulk Orders</span>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.95] mt-5">Wedding tomorrow?<br/>Birthday next Sunday?</h1>
          <p className="mt-4 max-w-xl opacity-90">From 5L party tubs to 10L wedding packs, branded ribbons & same-day delivery — give us 24 hours and we'll handle the freeze.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#bulk-form" className="inline-flex items-center gap-2 bg-white text-holiday-red px-6 py-3 rounded-full font-bold">Get a quote</a>
            <a href="tel:9235372759" className="inline-flex items-center gap-2 bg-choc text-cream px-6 py-3 rounded-full font-bold"><Phone className="w-4 h-4"/> 9235372759</a>
          </div>
          <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-sun-yellow opacity-30"/>
        </div>

        {/* Pricing tiers */}
        <div className="grid md:grid-cols-3 gap-5 mb-14" data-testid="bulk-tiers">
          {[
            { tag: "Most popular", title: "Party Tub 5L", price: "₹425", desc: "Serves 35–40. Single flavour.", tint: "bg-mint" },
            { tag: "Best value",   title: "Wedding Pack 10L", price: "₹700", desc: "Serves 70+. Mix any 2 flavours.", tint: "bg-ice-pink" },
            { tag: "Office combo", title: "Corporate Pack",   price: "₹500", desc: "50 cups + 2 jumbo tubs.",   tint: "bg-sun-yellow-soft" },
          ].map((t, i) => (
            <article key={i} className={`${t.tint} rounded-[2rem] p-7 cushion border border-[#F0E4E4]`} data-testid={`bulk-tier-${i}`}>
              <span className="sticker bg-cream text-choc">{t.tag}</span>
              <h3 className="font-display text-3xl mt-4">{t.title}</h3>
              <p className="font-display text-5xl text-holiday-red mt-2">{t.price}</p>
              <p className="text-sm text-choc/80 mt-2">{t.desc}</p>
            </article>
          ))}
        </div>

        {/* Form */}
        <div id="bulk-form" className="grid lg:grid-cols-12 gap-8">
          <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-[2rem] p-7 md:p-10 cushion border border-[#F0E4E4] space-y-4" data-testid="bulk-order-form">
            <h2 className="font-display text-3xl mb-2">Tell us about your event</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Name *</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="bulk-name-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Phone *</label>
                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required data-testid="bulk-phone-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Email (optional)</label>
              <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="bulk-email-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Event type *</label>
                <select value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} data-testid="bulk-event-type-select" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream">
                  {eventTypes.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Event date</label>
                <input type="date" value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} data-testid="bulk-date-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Approx. quantity / no. of guests *</label>
              <input value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} required placeholder="e.g. 80 guests OR 10L mixed" data-testid="bulk-quantity-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Notes (flavours, delivery address, theme)</label>
              <textarea rows={4} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="bulk-notes-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream resize-none"/>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" disabled={busy} data-testid="bulk-submit-btn" className="inline-flex items-center gap-2 bg-holiday-red text-white px-7 py-3.5 rounded-full font-bold hover:bg-holiday-red-deep disabled:opacity-50">
                <Send className="w-4 h-4"/> {busy ? "Sending…" : "Send via WhatsApp"}
              </button>
              <a href="tel:9235372759" className="inline-flex items-center gap-2 bg-choc text-cream px-7 py-3.5 rounded-full font-bold">
                <Phone className="w-4 h-4"/> Or call now
              </a>
            </div>
          </form>

          <aside className="lg:col-span-5 space-y-4">
            <div className="bg-ice-pink rounded-[2rem] p-7 cushion border border-[#F0E4E4]">
              <Truck className="w-5 h-5 text-holiday-red"/>
              <h3 className="font-display text-2xl mt-3">Same-day delivery</h3>
              <p className="text-sm text-choc/80 mt-2">Orders before 11am get delivered the same day across Gorakhpur. Cold-chain protected.</p>
            </div>
            <div className="bg-mint rounded-[2rem] p-7 cushion border border-[#F0E4E4]">
              <BadgePercent className="w-5 h-5 text-holiday-red"/>
              <h3 className="font-display text-2xl mt-3">Bulk discount auto-applied</h3>
              <p className="text-sm text-choc/80 mt-2">5L+ — 10% off. 10L+ — 15% off. Wedding/school/corporate — special rate on call.</p>
            </div>
            <div className="bg-sun-yellow-soft rounded-[2rem] p-7 cushion border border-[#F0E4E4]">
              <Award className="w-5 h-5 text-holiday-red"/>
              <h3 className="font-display text-2xl mt-3">Branded extras (free)</h3>
              <p className="text-sm text-choc/80 mt-2">Cups, wooden spoons, themed ribbons & sparklers — included in every wedding pack.</p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

